import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import isError from 'lodash/isError'
import isString from 'lodash/isString'
import { AlertTriangle, ArrowUpRight, Check, Circle, Download, FileWarning, Maximize2, Minimize2 } from 'lucide-react'
import { Link } from 'react-router'
import {
  PERMISSION_ACTION_APPROVE,
  PERMISSION_ACTION_DENY,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_RESET,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_ORDER,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER,
  ROUTE_DOCUMENT_REVIEW
} from '@klear/constants'
import { INCOTERM_CODES, INCOTERM_OPTIONS } from '@klear/constants/options'
import {
  ORDER_EVENT_STATUS_APPROVED,
  ORDER_EVENT_STATUS_DENIED,
  ORDER_EVENT_STATUS_EXPIRED
} from '@klear/constants/order-event-status'
import { KlearDate } from '@klear/date'
import {
  invoiceEventFormFromInvoice,
  invoiceEventFormToUpdateWire,
  validateInvoiceEventForm
} from '@klear/invoice/wire.client'
import type { FundReadinessRow } from '@klear/invoice-offer/fund-readiness'
import { Money } from '@klear/money'
import { ORDER_EVENT_INTERNAL_NOTE_FIELDS } from '@klear/order-event/constants'
import { EMPTY_ORDER_EVENT_ACTION_FORM } from '@klear/order-event/wire.client'
import type { OrderEventTimelineItem } from '@klear/order-event/order-event-timeline-tree'
import { timelineItemId } from '@klear/order-event/order-event-timeline-tree'
import { useEventFormState, type EventFormErrorMap } from '@klear/order-event-ui/event-shared/event-form-state'
import { useEventFormSave, type RunEventCommit } from '@klear/order-event-ui/event-shared/use-event-form-save'
import { proofOfAcceptanceFormToUpdateWire, proofOfAcceptanceToEventForm } from '@klear/proof-of-acceptance/wire.client'
import {
  purchaseOrderEventFormToUpdateWire,
  purchaseOrderToEventForm,
  validatePurchaseOrderEventForm
} from '@klear/purchase-order/wire.client'
import { buildDocumentFileUrl } from '@klear/upload/file-url'
import type { OrderEventDocumentRef } from '@klear/upload/order-event-documents'
import { resolveOrderEventPreviewFileUrl } from '@klear/ui/order-event-file-preview'
import { useConfirmDialog } from '@klear/ui/components/alert-dialog'
import { Button } from '@klear/ui/components/button'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { savedCustomerOptions, useCustomers } from '@klear/customer-ui/use-customers'
import { Input } from '@klear/ui/components/input'
import { MoneyInput } from '@klear/ui/components/money-input'
import { SheetDescription, SheetTitle } from '@klear/ui/components/sheet'
import { Skeleton } from '@klear/ui/components/skeleton'
import { Textarea } from '@klear/ui/components/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@klear/ui/components/tooltip'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useOrderActions } from '@klear/ui/hooks/use-order-actions'
import { useOrderEventInvalidate } from '@klear/ui/hooks/use-order-event-invalidate'
import { toast } from '@klear/ui/hooks/use-toast'
import { cn } from '@klear/ui/lib/utils'
import { trpc } from '@klear/ui/lib/trpc'
import { formatDate, plural } from '@/app/receivables/fund-checks'
import { StatusPill } from '@/app/receivables/status-pill'
import { RejectDialog, type RejectableField } from '@/app/receivables/reject-dialog'
import { InternalNotes } from '@/app/receivables/internal-notes'
import { PdfDocumentView, type QuoteHighlight } from '@/app/document-review/pdf-document-view'
import { approvalKeyOf } from '@/app/document-review/approval-row'
import { APPROVE_KEY } from '@/app/document-review/approve-row'
import { highlightFill } from '@/app/document-review/highlight-fill'
import { useApprovalKeyboard } from '@/app/document-review/use-approval-keyboard'
import { doubtKindLabel } from '@/app/document-review/review-labels'
import { NOT_PREVIEWABLE_MESSAGE } from '@klear/upload/content-type'
import { isBrowserRenderable, isPdfDocument } from '@/app/document-review/source-document-file'
import {
  amountChangeOf,
  checkTag,
  highlightsOf,
  firstQuoteOf,
  reviewOfDocument,
  sheetChecks,
  type AmountChange,
  type FiledReview,
  type RecordReviewChecks,
  type SheetCheck
} from '@/app/receivables/review-checks'
import type { ReviewView } from '@/app/receivables/use-review-view'

export type Reviewing = {
  record: OrderEventTimelineItem
  canApprove: boolean
  canEdit: boolean
  canUndoRejection: boolean
}

const DOC_TITLES: Record<string, string> = {
  [PERMISSION_ENTITY_PURCHASE_ORDER]: 'Purchase order',
  [PERMISSION_ENTITY_INVOICE]: 'Invoice',
  [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: 'Proof of acceptance'
}

export const DOC_SHORT: Record<string, string> = {
  [PERMISSION_ENTITY_PURCHASE_ORDER]: 'PO',
  [PERMISSION_ENTITY_INVOICE]: 'Invoice',
  [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: 'POA'
}

type ReviewValues = {
  customer: number | null
  number: string
  amount: Money | null
  effectiveDate: KlearDate | null
  endDate: KlearDate | null
  incoterm: string
  notes: string
}

type FieldKey = keyof ReviewValues

type ReviewField = { key: FieldKey; label: string; pricing?: true }

const CUSTOMER_FIELD: ReviewField = { key: 'customer', label: 'Customer', pricing: true }

const NOTES_FIELD: ReviewField = { key: 'notes', label: 'Notes' }

function reviewFields(record: OrderEventTimelineItem): ReviewField[] {
  switch (record.type) {
    case PERMISSION_ENTITY_PURCHASE_ORDER:
      return [
        CUSTOMER_FIELD,
        { key: 'number', label: 'PO number' },
        { key: 'amount', label: 'Amount' },
        { key: 'effectiveDate', label: 'Issue date' },
        { key: 'endDate', label: 'Target delivery date' },
        { key: 'incoterm', label: 'Incoterm' },
        NOTES_FIELD
      ]
    case PERMISSION_ENTITY_INVOICE:
      return [
        CUSTOMER_FIELD,
        { key: 'number', label: 'Invoice number' },
        { key: 'amount', label: 'Amount', pricing: true },
        { key: 'effectiveDate', label: 'Invoice date', pricing: true },
        { key: 'endDate', label: 'Collection date', pricing: true },
        NOTES_FIELD
      ]
    case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
      return [CUSTOMER_FIELD, { key: 'effectiveDate', label: 'Acceptance date' }, NOTES_FIELD]
    default:
      return []
  }
}

function rejectableFields(fields: ReviewField[], short: string): RejectableField[] {
  return [
    { key: 'document', label: `${short} document` },
    ...fields.flatMap(({ key, label }) => {
      const note = ORDER_EVENT_INTERNAL_NOTE_FIELDS.find((field) => field === key)
      return note === undefined ? [] : [{ key: note, label }]
    })
  ]
}

function reviewValues(record: OrderEventTimelineItem, customer: number | null): ReviewValues {
  const blank: ReviewValues = {
    customer,
    number: '',
    amount: null,
    effectiveDate: null,
    endDate: null,
    incoterm: '',
    notes: ''
  }
  switch (record.type) {
    case PERMISSION_ENTITY_PURCHASE_ORDER: {
      const po = record.model
      return {
        ...blank,
        number: po.number ?? '',
        amount: po.amount,
        effectiveDate: po.effectiveDate,
        endDate: po.endDate ?? null,
        incoterm: po.incoterm ?? '',
        notes: po.notes ?? ''
      }
    }
    case PERMISSION_ENTITY_INVOICE: {
      const invoice = record.model
      return {
        ...blank,
        number: invoice.number ?? '',
        amount: invoice.amount,
        effectiveDate: invoice.effectiveDate,
        endDate: invoice.endDate,
        notes: invoice.notes ?? ''
      }
    }
    case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
      return { ...blank, effectiveDate: record.model.effectiveDate, notes: record.model.notes ?? '' }
    default:
      return blank
  }
}

function pricingWarning(row: FundReadinessRow, field: FieldKey): string {
  if (field === 'customer') {
    return `Moves ${row.orderIdDisplay ?? 'the order'} and every record on it to this customer. Customer credit drives pricing: auto-priced offers on it re-price after you approve; offers priced by hand don't.`
  }
  if (!row.priceIsEstimate)
    return `Changes pricing, but ${row.offerIdDisplay} is priced by hand and won't update on its own.`
  const accepted = row.acceptedAt == null ? '' : ' The trader accepted the current price.'
  return `Changes pricing: ${row.offerIdDisplay} re-prices after you approve.${accepted}`
}

function purchaseOrderForm(record: OrderEventTimelineItem, values: ReviewValues) {
  if (record.type !== PERMISSION_ENTITY_PURCHASE_ORDER) return null
  const form = purchaseOrderToEventForm(record.model)
  return {
    ...form,
    number: values.number,
    amount: values.amount ?? form.amount,
    effectiveDate: values.effectiveDate ?? form.effectiveDate,
    endDate: values.endDate ?? form.endDate,
    incoterm:
      values.incoterm === form.incoterm ? form.incoterm : INCOTERM_CODES.find((code) => code === values.incoterm),
    notes: values.notes
  }
}

function invoiceForm(record: OrderEventTimelineItem, values: ReviewValues) {
  if (record.type !== PERMISSION_ENTITY_INVOICE) return null
  const form = invoiceEventFormFromInvoice(record.model)
  return {
    ...form,
    number: values.number,
    amount: values.amount ?? form.amount,
    effectiveDate: values.effectiveDate ?? form.effectiveDate,
    endDate: values.endDate ?? form.endDate,
    notes: values.notes
  }
}

function proofForm(record: OrderEventTimelineItem, values: ReviewValues) {
  if (record.type !== PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE) return null
  const form = proofOfAcceptanceToEventForm(record.model)
  return { ...form, effectiveDate: values.effectiveDate ?? form.effectiveDate, notes: values.notes }
}

function validateReview(record: OrderEventTimelineItem, values: ReviewValues): EventFormErrorMap {
  const errors: EventFormErrorMap = {}
  if (values.effectiveDate == null) errors.effectiveDate = 'Enter a date.'
  if (values.customer == null) errors.customer = 'Choose a customer.'
  if (record.type !== PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE && values.endDate == null) errors.endDate = 'Enter a date.'
  const purchaseOrder = purchaseOrderForm(record, values)
  if (purchaseOrder != null) return { ...validatePurchaseOrderEventForm(purchaseOrder), ...errors }
  const invoice = invoiceForm(record, values)
  if (invoice != null) return { ...validateInvoiceEventForm(invoice), ...errors }
  return errors
}

function sameValue(a: ReviewValues[FieldKey], b: ReviewValues[FieldKey]): boolean {
  if (a instanceof Money && b instanceof Money) return a.equals(b)
  if (a instanceof KlearDate && b instanceof KlearDate) return a.isSame(b)
  return a === b
}

function shownValue(value: ReviewValues[FieldKey]): string {
  if (value == null || value === '') return '—'
  if (value instanceof Money) return value.toString()
  if (value instanceof KlearDate) return formatDate(value.toJSON())
  return `${value}`
}

function DocumentPreview({
  document,
  highlights,
  activeKey
}: {
  document: OrderEventDocumentRef
  highlights: readonly QuoteHighlight[]
  activeKey: string | null
}) {
  const [pdfFailed, setPdfFailed] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const frame = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const sync = () => setFullscreen(window.document.fullscreenElement === frame.current)
    window.document.addEventListener('fullscreenchange', sync)
    return () => window.document.removeEventListener('fullscreenchange', sync)
  }, [])
  const toggle = () => void (fullscreen ? window.document.exitFullscreen() : frame.current?.requestFullscreen())
  const url = resolveOrderEventPreviewFileUrl(buildDocumentFileUrl(document.documentKey, document.traderId))
  if (!isBrowserRenderable(document.name)) {
    return (
      <div className="flex h-[70vh] flex-col items-center justify-center gap-3 rounded border bg-background px-6 text-center">
        <FileWarning className="h-8 w-8 text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium">{NOT_PREVIEWABLE_MESSAGE}</span>
        <Button size="sm" variant="outline" asChild>
          <a href={url} download={document.name}>
            <Download className="mr-1 h-3.5 w-3.5" aria-hidden />
            Download
          </a>
        </Button>
      </div>
    )
  }
  return (
    <div ref={frame} className="relative h-[70vh] overflow-hidden rounded border bg-background">
      {isPdfDocument(document.name) && !pdfFailed ? (
        <PdfDocumentView
          url={url}
          highlights={highlights}
          activeKey={activeKey}
          onLoadError={() => setPdfFailed(true)}
        />
      ) : (
        <iframe title={document.name} src={url} className="h-full w-full bg-white" />
      )}
      <Button
        size="icon"
        variant="secondary"
        aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
        title={fullscreen ? 'Exit full screen (Esc)' : 'Full screen'}
        onClick={toggle}
        className="absolute right-3 top-3 h-8 w-8 shadow"
      >
        {fullscreen ? <Minimize2 className="h-4 w-4" aria-hidden /> : <Maximize2 className="h-4 w-4" aria-hidden />}
      </Button>
    </div>
  )
}

const FILED_AT_FORMAT = 'MMM D'

function amountChangeText(change: AmountChange): string {
  const value = new Money(change.documentsTotal).toString()
  if (change.changedBy == null) return `Documents total ${value}`
  return `Documents total ${value} · changed by ${change.changedBy}, ${change.changedAt.toLocalString(FILED_AT_FORMAT)}`
}

function ViewSwitch({ view, onChange }: { view: ReviewView; onChange: (view: ReviewView) => void }) {
  const option = (value: ReviewView, label: string) => (
    <button
      type="button"
      aria-pressed={view === value}
      onClick={() => onChange(value)}
      className={cn(
        'h-7 rounded-md px-3 text-xs font-medium',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        view === value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {label}
    </button>
  )
  return (
    <div role="group" aria-label="View" className="inline-flex gap-0.5 rounded-lg bg-muted p-0.5">
      {option('review', 'Review')}
      {option('plain', 'Plain')}
    </div>
  )
}

function PageButton({
  page,
  label,
  doubted,
  active,
  onClick
}: {
  page: number
  label: string
  doubted: boolean
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={`Show ${label} on the page`}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-full border px-2 text-xs font-medium tabular-nums',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        active
          ? cn('border-transparent text-neutral-900', highlightFill({ doubted, active: true, dimmed: false }))
          : 'bg-background hover:bg-muted'
      )}
    >
      <span
        className={cn('h-2 w-2 rounded-sm', highlightFill({ doubted, active: false, dimmed: false }))}
        aria-hidden
      />
      p. {page}
    </button>
  )
}

function CheckNote({ check }: { check: SheetCheck }) {
  const tag = checkTag(check)
  const answered = tag === 'Answered'
  return (
    <div className="grid gap-0.5 pt-0.5 text-xs">
      <span>
        <b className={answered ? 'font-semibold' : 'font-medium text-muted-foreground'}>{tag}</b>
        <span className="text-muted-foreground"> · {doubtKindLabel(check.kind)}</span>
      </span>
      <span className={answered ? 'text-foreground/80' : 'text-muted-foreground'}>{check.message}</span>
      {check.answer != null ? <span className="text-foreground/80">Answer: {check.answer}</span> : null}
      {check.quote != null ? <span className="text-muted-foreground">“{check.quote}”</span> : null}
    </div>
  )
}

function DoubtMark({ checks, unsure }: { checks: readonly SheetCheck[]; unsure: boolean }) {
  if (checks.length === 0 && !unsure) return null
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span role="img" aria-label="Doubts" className="inline-flex text-warning">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent className="grid max-w-xs gap-1">
        {unsure ? <span>The run was unsure of this read.</span> : null}
        {checks.map((check, index) => (
          <span key={`${check.reviewId}-${index}`}>
            <b className="font-semibold">{doubtKindLabel(check.kind)}.</b> {check.message}
          </span>
        ))}
      </TooltipContent>
    </Tooltip>
  )
}

function FiledByLine({ review, named }: { review: FiledReview; named: boolean }) {
  const who = review.filedAutomatically ? 'Filed automatically' : `Filed by ${review.filedBy ?? 'a reviewer'}`
  const when = review.filedAt == null ? '' : `, ${review.filedAt.toLocalString(FILED_AT_FORMAT)}`
  return (
    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
      <span className="min-w-0 break-words">
        {named ? `${review.uploadedFilename ?? 'Document'} · ` : ''}
        {who}
        {when}
      </span>
      <Button size="sm" variant="outline" asChild className="shrink-0">
        <Link to={`/${ROUTE_DOCUMENT_REVIEW}/${review.reviewId}`} target="_blank" rel="noreferrer">
          Open review
          <ArrowUpRight className="ml-1 h-3.5 w-3.5" aria-hidden />
        </Link>
      </Button>
    </div>
  )
}

export function ReviewSheet({
  row,
  reviewing,
  documents,
  loadingDocuments,
  documentId,
  checks,
  checksFailed,
  view,
  onViewChange,
  onClose,
  onApproved
}: {
  row: FundReadinessRow
  reviewing: Reviewing
  documents: readonly OrderEventDocumentRef[]
  loadingDocuments: boolean
  documentId: string | null
  checks: RecordReviewChecks | undefined
  checksFailed: boolean
  view: ReviewView
  onViewChange: (view: ReviewView) => void
  onClose: () => void
  onApproved: () => void
}) {
  const { record } = reviewing
  const recordId = timelineItemId(record)
  const traderId = row.traderId ?? 0
  const document = documents.find((one) => one.id === documentId) ?? documents[0]
  const inReview = view === 'review'
  const [initial, setInitial] = useState(() => reviewValues(record, row.customerId))
  const [fields] = useState(() => reviewFields(record))
  const keys = fields.map((field) => field.key)
  const found = sheetChecks(checks, keys)
  const shownReview = reviewOfDocument(checks, document?.id)
  const highlights = useMemo(() => (inReview ? highlightsOf(shownReview) : []), [inReview, shownReview])
  const form = useEventFormState<ReviewValues>({ initial })
  const [busy, setBusy] = useState(false)
  const { approveEvent, resetEvent } = useOrderActions()
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const { save } = useEventFormSave()
  const invalidate = useOrderEventInvalidate()
  const { user } = useAuth()
  const canEdit = user.hasPermission(record.type, PERMISSION_ACTION_EDIT)
  const canEditCustomer = user.hasPermission(PERMISSION_ENTITY_ORDER, PERMISSION_ACTION_EDIT)
  const { customers, isLoading: customersLoading } = useCustomers(traderId)
  const customerOptions: ComboboxOption<number>[] = savedCustomerOptions(customers)
  const setCustomer = trpc.order.setCustomer.useMutation()
  const updatePurchaseOrder = trpc.purchaseOrder.update.useMutation()
  const commitInvoice = trpc.invoice.commit.useMutation()
  const commitProof = trpc.proofOfAcceptance.commit.useMutation()
  const edited = fields.filter((field) => !sameValue(form.values[field.key], initial[field.key]))
  const problems = validateReview(record, form.values)
  const noDocument = !loadingDocuments && documents.length === 0
  const locked = !reviewing.canEdit
  const title = DOC_TITLES[record.type] ?? 'Record'
  const short = DOC_SHORT[record.type] ?? 'Record'
  const noteFields = rejectableFields(fields, short)
  const [rejecting, setRejecting] = useState(false)
  const status = record.envelope.status
  const canApprove = reviewing.canApprove && status !== ORDER_EVENT_STATUS_APPROVED
  const canReject =
    canApprove &&
    status !== ORDER_EVENT_STATUS_DENIED &&
    status !== ORDER_EVENT_STATUS_EXPIRED &&
    user.hasPermission(record.type, PERMISSION_ACTION_DENY)
  const eventRef = { id: recordId, orderId: row.orderId, traderId, type: record.type }
  const rejected = status === ORDER_EVENT_STATUS_DENIED
  const canUndoRejection = rejected && reviewing.canUndoRejection
  const walking = !locked
  const [active, setActive] = useState<string | null>(walking ? (keys[0] ?? null) : null)
  const [focusOnOpen] = useState(active)
  const [checked, setChecked] = useState<ReadonlySet<string>>(() => new Set(canApprove ? [] : keys))
  const remaining = walking ? keys.filter((key) => !checked.has(key)).length : 0
  const activeKey = inReview && active != null && firstQuoteOf(shownReview, active) != null ? active : null
  const focusRow = useCallback((node: HTMLDivElement | null) => node?.focus(), [])

  const confirmField = (key: string) => {
    if (walking) setChecked((current) => new Set(current).add(key))
  }

  const setChecking = (key: string, on: boolean) =>
    setChecked((current) => {
      if (current.has(key) === on) return current
      const next = new Set(current)
      if (on) next.add(key)
      else next.delete(key)
      return next
    })

  const edit = <K extends FieldKey>(key: K, value: ReviewValues[K]) => {
    form.setField(key, value)
    setChecking(key, false)
  }

  const keyboard = useApprovalKeyboard({
    keys,
    isOpen: (key) => walking && !checked.has(key),
    finishKey: walking ? APPROVE_KEY : null,
    onConfirm: confirmField,
    onToggle: (key) => {
      if (walking) setChecking(key, !checked.has(key))
    },
    onEdit: (key) =>
      keyboard.containerRef.current
        ?.querySelector<HTMLElement>(`[data-approval-key="${key}"] [data-review-editor] :is(input, textarea, button)`)
        ?.focus(),
    onFinish: () => {
      if (!walking || active == null) return
      confirmField(active)
      keyboard.focusAfter(active)
    }
  })

  const undoRejection = () =>
    confirm({
      title: `Undo the rejection of ${record.envelope.idDisplay}?`,
      description: `The ${title.toLowerCase()} goes back to awaiting approval. The rejection and its notes stay in its history.`,
      confirmLabel: 'Undo rejection',
      onConfirm: async () => {
        setBusy(true)
        try {
          await resetEvent(row.orderId, eventRef, EMPTY_ORDER_EVENT_ACTION_FORM(PERMISSION_ACTION_RESET))
        } catch {
          await invalidate()
          return
        } finally {
          setBusy(false)
        }
        toast({ title: `${short} rejection undone`, description: 'It is awaiting approval again.' })
        onClose()
      }
    })

  const saveEdits = async () => {
    const parentEventId = record.envelope.parentId
    const values = form.values
    if (values.customer != null && values.customer !== initial.customer) {
      await setCustomer.mutateAsync({ orderId: row.orderId, traderId, customerId: values.customer })
      setInitial((saved) => ({ ...saved, customer: values.customer }))
    }
    if (edited.every((field) => field.key === 'customer')) return
    const runCommit: RunEventCommit = async () => {
      const purchaseOrder = purchaseOrderForm(record, values)
      const invoice = invoiceForm(record, values)
      const proof = proofForm(record, values)
      if (purchaseOrder != null) {
        await updatePurchaseOrder.mutateAsync({
          id: recordId,
          traderId,
          data: purchaseOrderEventFormToUpdateWire(purchaseOrder)
        })
      } else if (invoice != null) {
        await commitInvoice.mutateAsync({
          id: recordId,
          traderId,
          data: invoiceEventFormToUpdateWire(invoice, parentEventId)
        })
      } else if (proof != null) {
        await commitProof.mutateAsync({
          id: recordId,
          traderId,
          data: proofOfAcceptanceFormToUpdateWire(proof, parentEventId)
        })
      }
      return { id: recordId }
    }
    await save({ traderId, eventId: recordId, keptDocumentIds: [], originalDocumentIds: [], files: [], runCommit })
  }

  const approve = async () => {
    setBusy(true)
    const editCount = edited.length
    try {
      if (editCount > 0) {
        try {
          await saveEdits()
        } catch (error) {
          await invalidate()
          toast({
            title: `${short} not saved`,
            description: isError(error) ? error.message : 'Try again.',
            variant: 'destructive'
          })
          return
        }
        setInitial(form.values)
      }
      try {
        await approveEvent(row.orderId, eventRef, EMPTY_ORDER_EVENT_ACTION_FORM(PERMISSION_ACTION_APPROVE))
      } catch {
        if (editCount > 0) {
          toast({ title: `${short} edits saved`, description: 'Approving failed. Try Approve again.' })
        }
        await invalidate()
        return
      }
      toast({
        title: `${short} approved`,
        description: editCount > 0 ? `${plural(editCount, 'field')} edited before approving.` : 'No fields changed.'
      })
      onApproved()
    } finally {
      setBusy(false)
    }
  }

  const saveOnly = async () => {
    setBusy(true)
    const editCount = edited.length
    try {
      await saveEdits()
    } catch (error) {
      await invalidate()
      toast({
        title: `${short} not saved`,
        description: isError(error) ? error.message : 'Try again.',
        variant: 'destructive'
      })
      return
    } finally {
      setBusy(false)
    }
    toast({ title: `${short} saved`, description: `${plural(editCount, 'field')} edited.` })
    onClose()
  }

  const approveLabel = edited.length > 0 ? `Approve with ${plural(edited.length, 'edit')}` : 'Approve'
  const finishDisabled = busy || remaining > 0 || edited.some((field) => problems[field.key] != null)
  const finishTitle = remaining > 0 ? `Check ${plural(remaining, 'more field')} first` : undefined

  return (
    <>
      <div
        ref={keyboard.containerRef}
        onKeyDown={keyboard.onKeyDown}
        onFocus={(event) => {
          const key = approvalKeyOf(event.target)
          if (key != null && keys.some((one) => one === key)) setActive(key)
        }}
        className="contents"
      >
        <div className="col-span-2 flex flex-wrap items-center justify-between gap-3 pr-8">
          <div className="min-w-0">
            <SheetDescription className="text-sm text-muted-foreground">
              {[row.offerIdDisplay, row.traderName, row.customerName].filter(Boolean).join(' · ')}
            </SheetDescription>
            <SheetTitle className="mt-1 break-words text-lg font-semibold">
              {title}
              {document != null ? ` · ${document.name}` : ''}
            </SheetTitle>
          </div>
          <ViewSwitch view={view} onChange={onViewChange} />
        </div>

        <div className="col-span-2 grid min-h-0 grid-cols-1 items-start gap-6 overflow-y-auto overflow-x-hidden md:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
          <div className="grid gap-2">
            {loadingDocuments && document == null ? <Skeleton className="h-[70vh] w-full" /> : null}
            {noDocument ? (
              <div className="grid h-[60vh] place-items-center rounded border p-8 text-center text-muted-foreground">
                No document is attached to {record.envelope.idDisplay}.
              </div>
            ) : null}
            {document != null ? (
              <DocumentPreview key={document.id} document={document} highlights={highlights} activeKey={activeKey} />
            ) : null}
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 pr-2">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold">
                {locked || noDocument ? `${short} values` : 'Check against the document'}
              </h4>
              {walking ? (
                <span className="text-xs tabular-nums text-muted-foreground" aria-live="polite">
                  {keys.length - remaining} of {keys.length} checked
                </span>
              ) : null}
              {status === ORDER_EVENT_STATUS_APPROVED ? <StatusPill tone="ok">Approved</StatusPill> : null}
              {rejected ? <StatusPill tone="bad">Rejected</StatusPill> : null}
            </div>
            {inReview && checksFailed ? (
              <p className="text-xs text-danger">{"Review checks didn't load. Reopen the sheet to try again."}</p>
            ) : null}
            {inReview && found.fieldless.length > 0 ? (
              <div className="grid gap-1.5 rounded-md bg-muted px-3 py-2.5">
                {found.fieldless.map((check, index) => (
                  <CheckNote key={`${check.reviewId}-${index}`} check={check} />
                ))}
              </div>
            ) : null}
            <TooltipProvider delayDuration={0}>
              <form
                className="grid grid-cols-[minmax(0,1fr)] gap-3"
                aria-label={`${title} fields`}
                onSubmit={(event) => event.preventDefault()}
              >
                {fields.map((field) => {
                  const id = `review-${recordId}-${field.key}`
                  const value = form.values[field.key]
                  const changed = !sameValue(value, initial[field.key])
                  const problem = locked || !changed ? null : problems[field.key]
                  const disabled = locked || !(field.key === 'customer' ? canEditCustomer : canEdit)
                  const was = field.key === 'customer' ? (row.customerName ?? '—') : shownValue(initial[field.key])
                  const shown =
                    field.key === 'customer'
                      ? (customerOptions.find((option) => option.value === form.values.customer)?.label ??
                        row.customerName ??
                        '—')
                      : shownValue(value)
                  const flagged = cn(
                    'disabled:cursor-default disabled:bg-muted disabled:opacity-100',
                    (changed || problem != null) && 'border-warning'
                  )
                  let input = (
                    <Input
                      id={id}
                      value={isString(value) ? value : ''}
                      disabled={disabled}
                      onChange={(event) => edit(field.key, event.target.value)}
                      className={flagged}
                    />
                  )
                  if (field.key === 'customer') {
                    input = (
                      <Combobox<number>
                        options={customerOptions}
                        value={form.values.customer ?? ''}
                        onChange={(next) => edit('customer', next === '' ? null : next)}
                        valueType="number"
                        triggerLabel={customersLoading ? 'Loading…' : 'Choose a customer…'}
                        placeholder="Search customers…"
                        disabled={disabled}
                        className={flagged}
                      />
                    )
                  } else if (field.key === 'notes') {
                    input = (
                      <Textarea
                        id={id}
                        value={form.values.notes}
                        disabled={disabled}
                        onChange={(event) => edit('notes', event.target.value)}
                        maxLength={4000}
                        rows={3}
                        className={flagged}
                      />
                    )
                  } else if (value instanceof Money) {
                    input = (
                      <MoneyInput
                        id={id}
                        label=""
                        value={value}
                        disabled={disabled}
                        onChange={(next) => edit('amount', next)}
                        inputClassName={flagged}
                        inputContainerClassName="flex-1"
                      />
                    )
                  } else if (field.key === 'incoterm') {
                    input = (
                      <Combobox<string>
                        options={[...INCOTERM_OPTIONS]}
                        value={form.values.incoterm}
                        onChange={(next) => edit('incoterm', next)}
                        triggerLabel={form.values.incoterm || 'Choose an incoterm…'}
                        placeholder="Search incoterms…"
                        disabled={disabled}
                        className={flagged}
                      />
                    )
                  } else if (field.key === 'effectiveDate' || field.key === 'endDate') {
                    const date = form.values[field.key]
                    input = (
                      <Input
                        id={id}
                        type="date"
                        value={date?.toJSON() ?? ''}
                        disabled={disabled}
                        onChange={(event) => edit(field.key, KlearDate.parse(event.target.value) ?? null)}
                        className={flagged}
                      />
                    )
                  }
                  const quoted = inReview ? firstQuoteOf(shownReview, field.key) : undefined
                  const amountChange =
                    inReview && field.key === 'amount' ? amountChangeOf(checks, initial.amount) : null
                  const fieldChecks = inReview ? (found.byField.get(field.key) ?? []) : []
                  const isActive = active === field.key
                  const editing = walking && isActive
                  const isChecked = checked.has(field.key)
                  const confirmAndMove = () => {
                    confirmField(field.key)
                    keyboard.focusAfter(field.key)
                  }
                  return (
                    <div
                      key={field.key}
                      ref={field.key === focusOnOpen ? focusRow : undefined}
                      data-approval-key={field.key}
                      tabIndex={walking ? 0 : -1}
                      role="group"
                      aria-label={`${field.label}: ${shown}${isChecked ? ', checked' : ''}`}
                      aria-current={isActive || undefined}
                      className={cn(
                        '-m-2 grid grid-cols-[minmax(0,1fr)] gap-1 rounded-md p-2 outline-none',
                        'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                        isActive ? 'bg-muted/60 ring-1 ring-inset ring-amber-500/60' : 'hover:bg-muted/50',
                        walking && !isActive && 'cursor-pointer'
                      )}
                    >
                      <div className="flex min-h-[22px] items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1.5">
                          {walking ? (
                            <button
                              type="button"
                              tabIndex={-1}
                              aria-hidden
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                keyboard.focusKey(field.key)
                                setChecking(field.key, !isChecked)
                              }}
                              className="flex h-4 w-4 shrink-0 items-center justify-center"
                            >
                              {isChecked ? (
                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                              ) : (
                                <Circle className="h-3 w-3 text-muted-foreground/60" />
                              )}
                            </button>
                          ) : null}
                          <label htmlFor={editing ? id : undefined} className="text-xs text-muted-foreground">
                            {field.label}
                          </label>
                          <DoubtMark checks={fieldChecks} unsure={quoted?.confidence === 'low'} />
                        </span>
                        {quoted != null ? (
                          <PageButton
                            page={quoted.page}
                            doubted={quoted.confidence === 'low'}
                            label={field.label}
                            active={activeKey === field.key}
                            onClick={() => keyboard.focusKey(field.key)}
                          />
                        ) : null}
                      </div>
                      {editing ? (
                        <div
                          data-review-editor
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter' || event.metaKey || event.ctrlKey) return
                            if (!(event.target instanceof HTMLInputElement)) return
                            if (!event.currentTarget.contains(event.target)) return
                            event.preventDefault()
                            confirmAndMove()
                          }}
                        >
                          {input}
                        </div>
                      ) : (
                        <span
                          className={cn(
                            'whitespace-pre-wrap break-words text-sm',
                            shown === '—' ? 'text-muted-foreground' : 'font-medium',
                            isChecked && 'text-foreground/75'
                          )}
                        >
                          {shown}
                        </span>
                      )}
                      {changed ? <span className="text-xs text-warning">Was {was}</span> : null}
                      {amountChange != null ? (
                        <span className="text-xs text-warning">{amountChangeText(amountChange)}</span>
                      ) : null}
                      {isActive || !walking
                        ? fieldChecks.map((check, index) => (
                            <CheckNote key={`${check.reviewId}-${index}`} check={check} />
                          ))
                        : null}
                      {changed && field.pricing ? (
                        <span className="text-xs text-warning">{pricingWarning(row, field.key)}</span>
                      ) : null}
                      {problem ? <span className="text-xs text-warning">{problem}</span> : null}
                      {editing ? (
                        <div className="flex justify-end pt-1">
                          <Button
                            type="button"
                            size="xs"
                            variant={isChecked ? 'outline' : 'default'}
                            onClick={confirmAndMove}
                          >
                            {isChecked ? 'Next' : 'Confirm'}
                            <kbd className="ml-1.5 font-sans opacity-70">↵</kbd>
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </form>
            </TooltipProvider>
            {walking ? (
              <p className="text-xs text-muted-foreground">
                Enter confirms and moves on (⌘ Enter in a list or notes) · ↑ ↓ field · E edit · ← → document
              </p>
            ) : null}
            <p className="text-sm text-muted-foreground">
              {locked && !rejected ? 'Read-only.' : null}
              {rejected
                ? `Rejected. ${canUndoRejection ? 'Undo the rejection to send it back for approval. ' : ''}`
                : null}
              {canApprove && noDocument
                ? `Nothing to check these against yet. Approve once the trader's ${short} document is attached.`
                : null}
              {!locked && canApprove && !noDocument
                ? 'Check each field against the document, fix anything that is wrong, then approve.'
                : null}
              {!locked && !canApprove
                ? 'Fields stay editable until the offer is funded. Check each edit, then save.'
                : null}
            </p>
            {inReview && checks != null && checks.reviews.length > 0 ? (
              <div className="grid gap-2 border-t pt-3">
                {checks.reviews.map((review) => (
                  <FiledByLine key={review.reviewId} review={review} named={checks.reviews.length > 1} />
                ))}
              </div>
            ) : null}
            <InternalNotes orderEventId={recordId} fields={noteFields} />
          </div>
        </div>

        <div className="col-start-2 row-start-3 flex items-end border-t pt-3">
          <div className="flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="outline" onClick={onClose}>
              {locked ? 'Close' : 'Cancel'}
            </Button>
            {canUndoRejection ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={undoRejection}>
                Undo rejection
              </Button>
            ) : null}
            {canReject ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setRejecting(true)}>
                Reject
              </Button>
            ) : null}
            {canApprove ? (
              <Button
                size="sm"
                data-approval-key={APPROVE_KEY}
                title={finishTitle}
                disabled={finishDisabled || loadingDocuments || noDocument}
                onClick={() => void approve()}
              >
                {approveLabel}
              </Button>
            ) : walking ? (
              <Button
                size="sm"
                data-approval-key={APPROVE_KEY}
                title={finishTitle}
                disabled={finishDisabled || edited.length === 0}
                onClick={() => void saveOnly()}
              >
                {edited.length > 0 ? `Save ${plural(edited.length, 'edit')}` : 'Save'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      {canReject ? (
        <RejectDialog
          open={rejecting}
          onOpenChange={setRejecting}
          eventRef={eventRef}
          idDisplay={record.envelope.idDisplay}
          short={short}
          fields={noteFields}
          onRejected={() => {
            setRejecting(false)
            onClose()
          }}
        />
      ) : null}
      {ConfirmDialog}
    </>
  )
}
